import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiUpload, FiCheck, FiArrowRight } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { applyVendor } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PageTransition from '../components/common/PageTransition';
import './BecomeVendorPage.css';

const CATEGORIES = ['Electronics', 'Fashion', 'Home & Garden', 'Sports', 'Beauty', 'Books', 'Toys', 'Food', 'Automotive', 'Other'];

const BENEFITS = [
  { icon: '💰', title: 'Keep 90% Revenue',       desc: 'Only 10% platform commission — one of the lowest in the industry.' },
  { icon: '🚀', title: 'Free Setup',              desc: 'No listing fees, no setup costs. Start selling in minutes.' },
  { icon: '📊', title: 'Powerful Dashboard',      desc: 'Real-time analytics, order management, and inventory tracking.' },
  { icon: '🌍', title: 'Reach Millions',          desc: 'Access our growing customer base across Pakistan.' },
  { icon: '💳', title: 'Fast Payouts',            desc: 'Bi-weekly payouts directly to your bank account.' },
  { icon: '🛡️', title: 'Seller Protection',       desc: 'Dispute resolution and fraud protection built in.' },
];

const STEPS = ['Apply', 'Review (1-2 days)', 'Start Selling'];

export default function BecomeVendorPage() {
  const { user, vendor, updateVendor, updateUser } = useAuth();
  const navigate = useNavigate();
  const logoRef   = useRef(null);
  const bannerRef = useRef(null);

  const [form, setForm] = useState({ storeName: '', description: '', category: '' });
  const [logoFile,   setLogoFile]   = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [logoPreview,   setLogoPreview]   = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (vendor?.status === 'approved') navigate('/vendor/dashboard', { replace: true });
  }, [vendor, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleBanner = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.storeName.trim()) return toast.error('Store name is required');
    if (!user) {
      toast.error('Please log in first');
      navigate('/login', { state: { from: '/vendor/apply' } });
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('storeName', form.storeName.trim());
      fd.append('description', form.description.trim());
      fd.append('category', form.category);
      if (logoFile) fd.append('logo', logoFile);
      if (bannerFile) fd.append('banner', bannerFile);

      const { data } = await applyVendor(fd);
      updateVendor(data.vendor);
      updateUser({ role: 'vendor' });
      toast.success('Application submitted! We\'ll review it within 1-2 days.');
      navigate('/vendor/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  /* Already a vendor (pending/suspended) */
  if (vendor && vendor.status !== 'approved') {
    return (
      <div className="bv-page">
        <div className="bv-already card">
          <div className="bv-already-icon">⏳</div>
          <h2>Application {vendor.status === 'suspended' ? 'Suspended' : 'Under Review'}</h2>
          <p>
            {vendor.status === 'pending'
              ? 'Your vendor application is being reviewed. We\'ll notify you within 1-2 business days.'
              : vendor.status === 'rejected'
              ? 'Your application was not approved. Contact support for more details.'
              : 'Your account has been suspended. Contact support.'}
          </p>
          <Link to="/" className="btn btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <PageTransition>
    <div className="bv-page">
      {/* Hero */}
      <section className="bv-hero">
        <div className="bv-hero-inner container">
          <p className="bv-hero-eyebrow">Join 1,000+ sellers on fivora</p>
          <h1 className="bv-hero-title">Start Selling Today.<br />Grow Your Business.</h1>
          <p className="bv-hero-sub">Apply in minutes. No fees. No limits.</p>
          <div className="bv-steps">
            {STEPS.map((step, i) => (
              <div key={step} className="bv-step">
                <div className="bv-step-num">{i + 1}</div>
                <span className="bv-step-label">{step}</span>
                {i < STEPS.length - 1 && <FiArrowRight size={14} className="bv-step-arrow" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bv-benefits-section container">
        <h2 className="bv-section-title">Why sell on fivora?</h2>
        <div className="bv-benefits-grid">
          {BENEFITS.map((b) => (
            <div key={b.title} className="bv-benefit-card card">
              <span className="bv-benefit-icon">{b.icon}</span>
              <h3 className="bv-benefit-title">{b.title}</h3>
              <p className="bv-benefit-desc">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section className="bv-form-section container" id="apply">
        <div className="bv-form-wrap">
          <div className="bv-form-header">
            <h2 className="bv-section-title" style={{ margin: 0 }}>Apply Now</h2>
            <p className="bv-form-sub">Fill in your store details to get started.</p>
          </div>

          {!user && (
            <div className="bv-login-prompt card">
              You need to be logged in to apply.{' '}
              <Link to="/login" state={{ from: '/vendor/apply' }} className="text-primary font-medium">
                Log in or create an account
              </Link>
            </div>
          )}

          <form className="card bv-form" onSubmit={handleSubmit}>
            {/* Banner */}
            <div className="bv-banner-wrap">
              <div
                className="bv-banner"
                style={bannerPreview ? { backgroundImage: `url(${bannerPreview})` } : {}}
                onClick={() => bannerRef.current?.click()}
              >
                {!bannerPreview && (
                  <div className="bv-banner-placeholder">
                    <FiUpload size={22} />
                    <span>Upload Store Banner <span className="bv-opt">(optional)</span></span>
                  </div>
                )}
                <input ref={bannerRef} type="file" accept="image/*" hidden onChange={handleBanner} />
              </div>

              {/* Logo */}
              <div className="bv-logo-wrap">
                <div className="bv-logo" onClick={() => logoRef.current?.click()}>
                  {logoPreview
                    ? <img src={logoPreview} alt="logo" />
                    : <span>{form.storeName?.[0]?.toUpperCase() || '+'}</span>}
                </div>
                <input ref={logoRef} type="file" accept="image/*" hidden onChange={handleLogo} />
                <p className="bv-logo-hint">Logo</p>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Store Name *</label>
              <input
                name="storeName"
                className="form-input"
                placeholder="e.g. TechZone Pro"
                value={form.storeName}
                onChange={handleChange}
                maxLength={80}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Category</label>
              <select name="category" className="form-select" value={form.category} onChange={handleChange}>
                <option value="">— What do you sell? —</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Store Description <span className="bv-opt">(optional)</span></label>
              <textarea
                name="description"
                className="form-textarea"
                rows={4}
                placeholder="Tell buyers what makes your store special…"
                value={form.description}
                onChange={handleChange}
              />
            </div>

            <div className="bv-tos">
              <FiCheck size={14} className="bv-tos-icon" />
              <span>By applying I agree to fivora's <Link to="/terms" className="text-primary">Seller Terms</Link> and <Link to="/privacy" className="text-primary">Privacy Policy</Link>.</span>
            </div>

            <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={submitting || !user}>
              {submitting ? 'Submitting…' : 'Submit Application'}
            </button>
          </form>
        </div>
      </section>
    </div>
    </PageTransition>
  );
}
